/**
 * Site + commercial property form for AOI Location (shown after fetch).
 * Side-by-side on desktop to avoid empty vertical space.
 */
import { Building2, MapPinned } from 'lucide-react';

export const PROPERTY_TYPES = [
    'Independent Building',
    'High Street',
    'Commercial Complex',
    'Mall',
    'Hospital Building',
    'Mixed Use Building',
];

export function emptySiteDetails() {
    return {
        city: '',
        state: '',
        locality: '',
        completeAddress: '',
        googleMapsLink: '',
        latitude: '',
        longitude: '',
        propertyName: '',
        landmark: '',
    };
}

export function emptyCommercialDetails() {
    return {
        monthlyRent: '',
        cam: '',
        securityDeposit: '',
        lockInYears: '',
        quotedRentPerSqFt: '',
        propertyTypes: [],
        totalSuperArea: '',
        carpetArea: '',
        frontage: '',
        ceilingHeight: '',
        roadWidth: '',
    };
}

/** Build auto site fields from selected place + autofetch reverse-geocode. */
export function buildSiteDetailsFromFetch({ selectedPlace, resolvedLocation, resolvedState, resolvedDistrict }) {
    const lat = selectedPlace?.lat ?? resolvedLocation?.lat;
    const lng = selectedPlace?.lng ?? resolvedLocation?.lon;
    const latN = Number(lat);
    const lngN = Number(lng);
    const hasCoords = Number.isFinite(latN) && Number.isFinite(lngN);

    return {
        city: resolvedLocation?.city
            || resolvedLocation?.territoryDistrict
            || resolvedDistrict
            || '',
        state: resolvedLocation?.state || resolvedState || '',
        locality: resolvedLocation?.locality
            || resolvedLocation?.localName
            || selectedPlace?.name
            || '',
        completeAddress: resolvedLocation?.completeAddress
            || resolvedLocation?.label
            || selectedPlace?.address
            || '',
        googleMapsLink: hasCoords
            ? `https://www.google.com/maps/search/?api=1&query=${latN},${lngN}`
            : '',
        latitude: hasCoords ? latN.toFixed(6) : '',
        longitude: hasCoords ? lngN.toFixed(6) : '',
        propertyName: resolvedLocation?.propertyName
            || selectedPlace?.name
            || '',
        landmark: resolvedLocation?.landmark
            || resolvedLocation?.road
            || '',
    };
}

function Field({ label, children, className = '', hint }) {
    return (
        <div className={className}>
            <label className="ag-label !mb-1">{label}</label>
            {children}
            {hint && <p className="text-[10px] text-ink-400 mt-1">{hint}</p>}
        </div>
    );
}

function SectionCard({ icon: Icon, title, badge, children, accent = 'teal' }) {
    const badgeCls = accent === 'rose'
        ? 'bg-rose-50 text-rose-800 ring-rose-200'
        : 'bg-brand-teal-50 text-brand-teal-800 ring-brand-teal/20';
    const iconCls = accent === 'rose' ? 'text-rose-700' : 'text-brand-teal-700';

    return (
        <section className="ag-card overflow-hidden shadow-soft h-full flex flex-col">
            <div className="px-4 py-3 border-b border-ink-100 bg-ink-50/70 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className={`h-4 w-4 shrink-0 ${iconCls}`} />
                    <h3 className="font-bold text-sm text-ink-900 truncate">{title}</h3>
                </div>
                {badge && (
                    <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ring-1 ${badgeCls}`}>
                        {badge}
                    </span>
                )}
            </div>
            <div className="p-4 flex-1 space-y-3">
                {children}
            </div>
        </section>
    );
}

export default function LocationPropertyForm({
    siteDetails,
    onSiteChange,
    commercialDetails,
    onCommercialChange,
    readOnly = false,
}) {
    const setSite = (key, value) => {
        if (readOnly) return;
        onSiteChange({ ...siteDetails, [key]: value });
    };
    const setCommercial = (key, value) => {
        if (readOnly) return;
        onCommercialChange({ ...commercialDetails, [key]: value });
    };

    const togglePropertyType = (type) => {
        if (readOnly) return;
        const current = commercialDetails.propertyTypes || [];
        const next = current.includes(type)
            ? current.filter((t) => t !== type)
            : [...current, type];
        setCommercial('propertyTypes', next);
    };

    return (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 items-stretch">
            <SectionCard icon={MapPinned} title="Site details" badge="Auto-filled">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <Field label="City">
                        <input className="ag-input text-sm py-2" value={siteDetails.city} onChange={(e) => setSite('city', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="State">
                        <input className="ag-input text-sm py-2" value={siteDetails.state} onChange={(e) => setSite('state', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Locality / Micro Market" className="sm:col-span-2">
                        <input className="ag-input text-sm py-2" value={siteDetails.locality} onChange={(e) => setSite('locality', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Complete Address" className="sm:col-span-2">
                        <textarea
                            className="ag-input text-sm min-h-[56px] py-2"
                            value={siteDetails.completeAddress}
                            onChange={(e) => setSite('completeAddress', e.target.value)}
                            readOnly={readOnly}
                        />
                    </Field>
                    <Field label="Property Name / Building Name">
                        <input className="ag-input text-sm py-2" value={siteDetails.propertyName} onChange={(e) => setSite('propertyName', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Landmark">
                        <input className="ag-input text-sm py-2" value={siteDetails.landmark} onChange={(e) => setSite('landmark', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Latitude">
                        <input className="ag-input text-sm font-mono py-2" value={siteDetails.latitude} onChange={(e) => setSite('latitude', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Longitude">
                        <input className="ag-input text-sm font-mono py-2" value={siteDetails.longitude} onChange={(e) => setSite('longitude', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Google Maps Link" className="sm:col-span-2">
                        <input className="ag-input text-xs font-mono py-2" value={siteDetails.googleMapsLink} onChange={(e) => setSite('googleMapsLink', e.target.value)} readOnly={readOnly} />
                    </Field>
                </div>
            </SectionCard>

            <SectionCard icon={Building2} title="Commercial terms" badge="Enter manually" accent="rose">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    <Field label="Monthly Rent (₹)">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.monthlyRent} onChange={(e) => setCommercial('monthlyRent', e.target.value)} placeholder="2,50,000" readOnly={readOnly} />
                    </Field>
                    <Field label="CAM (₹)">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.cam} onChange={(e) => setCommercial('cam', e.target.value)} placeholder="15,000" readOnly={readOnly} />
                    </Field>
                    <Field label="Security Deposit (₹)">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.securityDeposit} onChange={(e) => setCommercial('securityDeposit', e.target.value)} placeholder="5,00,000" readOnly={readOnly} />
                    </Field>
                    <Field label="Lock-in (years)">
                        <input type="number" min="0" step="0.5" className="ag-input text-sm py-2" value={commercialDetails.lockInYears} onChange={(e) => setCommercial('lockInYears', e.target.value)} placeholder="3" readOnly={readOnly} />
                    </Field>
                    <Field label="Quoted Rent / sq. ft. (₹)" className="sm:col-span-2">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.quotedRentPerSqFt} onChange={(e) => setCommercial('quotedRentPerSqFt', e.target.value)} placeholder="120" readOnly={readOnly} />
                    </Field>
                </div>

                <div>
                    <div className="ag-label !mb-1.5">Property Type</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                        {PROPERTY_TYPES.map((type) => {
                            const checked = (commercialDetails.propertyTypes || []).includes(type);
                            return (
                                <label
                                    key={type}
                                    className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 text-xs font-medium transition
                                        ${readOnly ? 'cursor-default' : 'cursor-pointer'}
                                        ${checked
                                            ? 'border-brand-teal bg-brand-teal-50 text-brand-teal-900'
                                            : 'border-ink-200 bg-white text-ink-700 hover:bg-ink-50'}`}
                                >
                                    <input
                                        type="checkbox"
                                        className="rounded border-ink-300 text-brand-teal focus:ring-brand-teal"
                                        checked={checked}
                                        disabled={readOnly}
                                        onChange={() => togglePropertyType(type)}
                                    />
                                    {type}
                                </label>
                            );
                        })}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-1 border-t border-ink-100">
                    <Field label="Total Super Area (sq. ft.)">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.totalSuperArea} onChange={(e) => setCommercial('totalSuperArea', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Carpet / Usable Area (sq. ft.)">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.carpetArea} onChange={(e) => setCommercial('carpetArea', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Frontage (ft.)">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.frontage} onChange={(e) => setCommercial('frontage', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Ceiling Height (ft.)">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.ceilingHeight} onChange={(e) => setCommercial('ceilingHeight', e.target.value)} readOnly={readOnly} />
                    </Field>
                    <Field label="Road Width (ft.)" className="sm:col-span-2">
                        <input type="number" min="0" className="ag-input text-sm py-2" value={commercialDetails.roadWidth} onChange={(e) => setCommercial('roadWidth', e.target.value)} readOnly={readOnly} />
                    </Field>
                </div>
            </SectionCard>
        </div>
    );
}
